/**
 * Word links: what joins two words in the town, each earned by a learning fact rather than by spelling alone.
 *   family    = two single words that are forms of one word (the Wiktionary lookup's lemma) or are built on one base
 *               word (their etymology: affix and compound parts, a Persian word's Arabic root). Words the lookup does
 *               not know pair only with each other, and only when long and alike (7+ letters, first 6 the same).
 *   contains  = a phrase holds a single-word item as written, or an inflected form of it (perdut → perdre)
 * Links feed the roads between moments (town-data.ts). Bridge materials, kinship and ferries are prototype only
 * (views/prototype-town/town-links.ts).
 */
import { classesOf, clauseGloss, faNorm, faStems, faWords, glossHint, lookup, lookupFa, phraseKey, type LexEntry } from './building-lexicon';
import type { Link, Tile } from './town-data';

// ---- word keys ------------------------------------------------------------------------------------------------

/** A Catalan article in front of a single word ("el préssec", "l'andana"), as town-data's core() drops it. */
const ARTICLE = /^(el|la|els|les|un|una|uns|unes|en|na|es|sa) |^(l|n)'(?=\p{L})/u;
const letters = (w: string) => [...w].filter((ch) => /\p{L}/u.test(ch)).length;

/** A word as the lookup keys it: Catalan lower case with its accents, Persian script without vowel marks or joiners. */
const keyOf = (w: string, language: string) => (language === 'fa' ? faNorm(w) : w);
/** An item's words (Persian as written, so its ezafe and suffix joiners still show). */
function wordsOf(text: string, language: string): string[] {
  if (language === 'fa') return faWords(text).filter((w) => faNorm(w));
  return phraseKey(text).split(/[\s']+/).filter((w) => /\p{L}/u.test(w));
}
/** The one word of a single-word item, article dropped; null for a phrase or a word too short to say much. */
function singleWord(text: string, language: string): string | null {
  if (language === 'fa') { const ws = faWords(text); return ws.length === 1 && letters(ws[0]) >= 2 ? ws[0] : null; }
  const w = phraseKey(text).replace(ARTICLE, '');
  return !/\s/.test(w) && letters(w) >= 3 ? w : null;
}
/** Parts of speech a verb form can also carry without being a different word. */
const VERBISH = new Set(['verb', 'adj', 'phrase']);
/** Little words: read as themselves inside a phrase, never as a form of a verb they happen to spell. */
const LITTLE = new Set(['pron', 'det', 'article', 'prep', 'conj', 'num', 'particle', 'classifier']);
const entryOf = (w: string, language: string): LexEntry | undefined => (language === 'fa' ? lookupFa(w) : lookup(language, w));

/**
 * The word and its lemmas; with `built`, also the base words it or its lemmas are built on. Null when the lookup
 * does not know the word. A lemma of the wrong class for the item's gloss is left out: vi "the wine" is not a form
 * of veure "to see".
 */
function keysOf(w: string, language: string, en: string | null, built: boolean): string[] | null {
  const e = entryOf(w, language);
  if (!e) return null;
  const hint = en ? glossHint(en) : null;
  // With no class in the gloss, a word that also reads as a noun or a little word (res "nothing", casa "home") is
  // not taken as a form of a verb (resar "to pray", casar "to marry") unless its gloss reads as a clause. A word
  // whose only other reading is an adjective keeps its verb: perdut "lost" is perdre's participle. Inside a phrase
  // (no gloss of its own) only a little word is read that way: res in "res més?" is "nothing", passa stays passar.
  const homograph = en ? !hint && !clauseGloss(en) && e.pos.some((p) => !VERBISH.has(p)) : e.pos.some((p) => LITTLE.has(p));
  const verbOnly = (w: string) => { const le = entryOf(w, language); return !!le && classesOf(le).every((c) => c === 'verb'); };
  const lemmas = (e.lemma ?? []).filter((l) => {
    const le = entryOf(l, language);
    if (!le) return true;
    return hint ? classesOf(le).includes(hint) : !(homograph && verbOnly(l));
  });
  // A Persian word found through its stem (written ezafe or suffix dropped) also answers to that stem.
  const stems = language === 'fa' && !lookup('fa', w) ? faStems(w).filter((k) => lookup('fa', k)) : [];
  const keys = new Set([keyOf(w, language), ...stems, ...lemmas]);
  // The same goes for the word's own base words: res is not built on resar.
  if (built) for (const x of [e, ...lemmas.map((l) => entryOf(l, language))]) for (const b of x?.base ?? []) if (!(x === e && homograph && verbOnly(b))) keys.add(b);
  return [...keys];
}
/** Words the lookup does not know pair only with each other, when long and alike: no false friends by spelling. */
const guessKey = (w: string): string | null => (letters(w) >= 7 ? `~${w.slice(0, 6)}` : null);

/**
 * Family and contains links between the town's tiles (in arrival order). A new word links to the first earlier
 * word holding each of its keys (the family head) and claims the keys nobody holds yet, so a link depends only on
 * earlier words and the growth slider never rewires the old ones.
 */
export function wordLinks(tiles: Tile[], language: string): Link[] {
  const links: Link[] = [];
  const owner = new Map<string, Tile>();
  const byWord = new Map<string, Tile[]>();
  for (const t of tiles) {
    const w = singleWord(t.item.ca, language);
    if (!w) continue;
    const keys = keysOf(w, language, t.item.en, true) ?? [keyOf(w, language), guessKey(keyOf(w, language))].filter((k): k is string => !!k);
    const heads = new Set<Tile>();
    for (const k of keys) { const h = owner.get(k); if (h) heads.add(h); else owner.set(k, t); }
    for (const h of heads) links.push({ a: h.key, b: t.key, kind: 'family' });
    byWord.set(keyOf(w, language), [...(byWord.get(keyOf(w, language)) ?? []), t]);
  }
  // Contains: each word of a phrase, as written or through its lemmas, against the single-word items.
  for (const t of tiles) {
    if (singleWord(t.item.ca, language) || !byWord.size) continue;
    const hits = new Set<Tile>();
    for (const p of new Set(wordsOf(t.item.ca, language))) {
      for (const k of keysOf(p, language, null, false) ?? [keyOf(p, language)]) for (const s of byWord.get(k) ?? []) if (s !== t) hits.add(s);
    }
    for (const s of hits) links.push({ a: s.key, b: t.key, kind: 'contains' });
  }
  return links;
}
